var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../config';
import { TableCell, TableRow } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { FirstCell, STICKIES, isOccupancy100Percent } from '../annualTable';
import { useControlledExpansion } from '../common/selectors';
import { getHighlightBorder } from '../common/utils';
import { CALCULATE_DIFF, CONSTRAINT_TYPE_COLOR, DECIMAL_VALUE, NEST_LEVEL, PERCENTAGES, metricTargetMap } from '../constants';
import { generateVariances } from '../selectors';
var ServiceStandardQueueRow = function (_a) {
  var queue = _a.queue,
    queueIdx = _a.queueIdx,
    keys = _a.keys,
    perScenarioQueues = _a.perScenarioQueues,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    data = _a.data,
    chunkSize = _a.chunkSize;
  var t = useTranslation('report', {
    keyPrefix: 'annual.reportRows'
  }).t;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var _b = useControlledExpansion(expandedProp, onToggle),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  var shouldHideAbandonmentRateRow = keys.includes('abandonmentRate') && !perScenarioQueues.some(function (scenQueues) {
    var _a;
    return (_a = scenQueues[queueIdx]) === null || _a === void 0 ? void 0 : _a.isAbandonable;
  });
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[3],
        stickySizes: STICKIES
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: handleToggle,
          bold: true
        }, {
          children: queue.name
        }))
      })), perScenarioQueues.map(function (_, index, arr) {
        var _a, _b;
        return _jsx(TableCell, {
          colSpan: 2,
          color: (_b = PLAN_COLORS[(_a = data[index]) === null || _a === void 0 ? void 0 : _a.colorIndex]) === null || _b === void 0 ? void 0 : _b[1],
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize)
        }, "".concat(queue.name, "-header-").concat(index));
      })]
    }), expanded && keys.map(function (key) {
      if (key === 'abandonmentRate' && shouldHideAbandonmentRateRow) return null;
      return _jsxs(TableRow, {
        children: [_jsx(FirstCell, {
          value: t(key),
          "aria-label": NEST_LEVEL[4],
          stickySizes: STICKIES
        }), perScenarioQueues.map(function (scenQueues, index, arr) {
          var _a, _b, _c, _d, _e, _f, _g;
          var q = scenQueues[queueIdx];
          if (!q) return _jsx(TableCell, {
            colSpan: 2
          }, "empty-".concat(key, "-").concat(index));
          var baseQueue = arr[index - index % chunkSize][queueIdx];
          var baseValue = baseQueue ? baseQueue[key] : null;
          var currentValue = q[key];
          var highlightBorder = getHighlightBorder(index, arr.length, chunkSize);
          // Occupancy-100% special case: render an empty cell for productiveHours
          if (key === 'productiveHours' && isOccupancy100Percent(q.occupancy)) {
            return _jsx(TableCell, {
              bold: !q.metricTarget,
              color: (_b = PLAN_COLORS[(_a = data[index]) === null || _a === void 0 ? void 0 : _a.colorIndex]) === null || _b === void 0 ? void 0 : _b[1],
              colSpan: 2,
              highlightBorder: highlightBorder
            }, "cell-".concat(key, "-").concat(index));
          }
          return _jsxs(React.Fragment, {
            children: [_jsx(TableCell, {
              bold: !q.metricTarget,
              color: (_d = PLAN_COLORS[(_c = data[index]) === null || _c === void 0 ? void 0 : _c.colorIndex]) === null || _d === void 0 ? void 0 : _d[1],
              sx: {
                minWidth: '6.4rem'
              },
              value: currentValue,
              percentage: PERCENTAGES.includes(key),
              decimal: (_e = DECIMAL_VALUE[key]) !== null && _e !== void 0 ? _e : 0,
              colSpan: key === 'serviceLevelForecast' ? 1 : 2,
              highlight: q.metricTarget && q.metricTarget === metricTargetMap[key],
              highlightBorder: key !== 'serviceLevelForecast' && highlightBorder,
              expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(key) ? generateVariances(baseValue, currentValue, isPercentages) : undefined,
              useColorValue: true
            }), key === 'serviceLevelForecast' && _jsx(TableCell, {
              sx: {
                minWidth: '6.4rem'
              },
              value: q.threshold,
              suffix: " sec",
              color: (_g = PLAN_COLORS[(_f = data[index]) === null || _f === void 0 ? void 0 : _f.colorIndex]) === null || _g === void 0 ? void 0 : _g[1],
              highlightBorder: highlightBorder,
              highlight: q.metricTarget === metricTargetMap[key],
              useColorValue: true
            })]
          }, "cell-".concat(key, "-").concat(index));
        })]
      }, key);
    })]
  });
};
export var ServiceStandardConstraintHeaderRow = function (_a) {
  var label = _a.label,
    expansionKey = _a.expansionKey,
    queues = _a.queues,
    perScenarioQueues = _a.perScenarioQueues,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keys = _a.keys;
  var resolvedExpansionKey = expansionKey !== null && expansionKey !== void 0 ? expansionKey : label;
  var getQueueExpansionItem = function (queue) {
    var _a;
    return "".concat(resolvedExpansionKey, ":queue:").concat((_a = queue.queueType) !== null && _a !== void 0 ? _a : '', ":").concat(queue.name);
  };
  var _b = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[resolvedExpansionKey], onToggleItem ? function () {
      return onToggleItem(resolvedExpansionKey);
    } : undefined),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  if (!queues.length) return null;
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[2],
        stickySizes: STICKIES,
        color: theme.palette.primary.light
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: handleToggle,
          bold: true
        }, {
          children: label
        }))
      })), data.map(function (_, index, arr) {
        var _a, _b, _c;
        return _jsx(TableCell, {
          colSpan: 2,
          color: (_c = (_b = PLAN_COLORS[(_a = data[index]) === null || _a === void 0 ? void 0 : _a.colorIndex]) === null || _b === void 0 ? void 0 : _b[1]) !== null && _c !== void 0 ? _c : CONSTRAINT_TYPE_COLOR,
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize)
        }, "def-nondef-".concat(label, "-").concat(index));
      })]
    }), expanded && queues.map(function (queue, queueIdx) {
      return _jsx(ServiceStandardQueueRow, {
        queue: queue,
        queueIdx: queueIdx,
        keys: keys,
        perScenarioQueues: perScenarioQueues,
        expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[getQueueExpansionItem(queue)],
        onToggle: onToggleItem ? function () {
          return onToggleItem(getQueueExpansionItem(queue));
        } : undefined,
        data: data,
        chunkSize: chunkSize
      }, "".concat(label, "-").concat(queue.name, "-").concat(queueIdx));
    })]
  });
};